import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  API_BASE,
  FALLBACK_CATALOG,
  fetchCardPreview,
  fetchCatalog,
  isValidDiscordId,
  type CardMeta,
} from "../lib/api";
import styles from "./Playground.module.css";

type ResultState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "done"; url: string; extension: string }
  | { status: "error"; error: string };

type FieldType = "text" | "color" | "number" | "select" | "boolean" | "date";

const BORDER_COLOR_SLOTS: { labelKey: string | null; placeholder: string }[] = [
  { labelKey: null, placeholder: "#7C5CFF" },
  { labelKey: "playground.borderColor2Label", placeholder: "#FF6EC7" },
  { labelKey: "playground.borderColor3Label", placeholder: "#22D3EE" },
  { labelKey: "playground.borderColor4Label", placeholder: "#FACC15" },
];

const THEME_OPTIONS = [
  { value: "", labelKey: "playground.fields.themeDefault" },
  { value: "discord", labelKey: "playground.fields.themeDiscord" },
  { value: "midnight", labelKey: "playground.fields.themeMidnight" },
  { value: "sunset", labelKey: "playground.fields.themeSunset" },
  { value: "neon", labelKey: "playground.fields.themeNeon" },
  { value: "forest", labelKey: "playground.fields.themeForest" },
  { value: "sakura", labelKey: "playground.fields.themeSakura" },
  { value: "monochrome", labelKey: "playground.fields.themeMonochrome" },
  { value: "gold", labelKey: "playground.fields.themeGold" },
];

const PRESENCE_OPTIONS = [
  { value: "", labelKey: "playground.fields.presenceDefault" },
  { value: "online", labelKey: "playground.fields.presenceOnline" },
  { value: "idle", labelKey: "playground.fields.presenceIdle" },
  { value: "dnd", labelKey: "playground.fields.presenceDnd" },
  { value: "offline", labelKey: "playground.fields.presenceOffline" },
  { value: "invisible", labelKey: "playground.fields.presenceInvisible" },
  { value: "streaming", labelKey: "playground.fields.presenceStreaming" },
  { value: "phone", labelKey: "playground.fields.presencePhone" },
];

const EFFECT_OPTIONS = [
  { value: "", labelKey: "playground.fields.effectNone" },
  { value: "shadow", labelKey: "playground.fields.effectShadow" },
  { value: "glow", labelKey: "playground.fields.effectGlow" },
];

const LAYOUT_OPTIONS = [
  { value: "", labelKey: "playground.fields.layoutCentered" },
  { value: "banner", labelKey: "playground.fields.layoutBanner" },
];

const WATERMARK_POSITION_OPTIONS = [
  { value: "", labelKey: "playground.fields.watermarkPositionDefault" },
  { value: "bottom-right", labelKey: "playground.fields.watermarkPositionBottomRight" },
  { value: "bottom-left", labelKey: "playground.fields.watermarkPositionBottomLeft" },
  { value: "top-right", labelKey: "playground.fields.watermarkPositionTopRight" },
  { value: "top-left", labelKey: "playground.fields.watermarkPositionTopLeft" },
  { value: "center", labelKey: "playground.fields.watermarkPositionCenter" },
  { value: "center-left", labelKey: "playground.fields.watermarkPositionCenterLeft" },
  { value: "center-right", labelKey: "playground.fields.watermarkPositionCenterRight" },
];

const SIZE_OPTIONS = [
  { value: "", labelKey: "playground.fields.sizeStandard" },
  { value: "compact", labelKey: "playground.fields.sizeCompact" },
  { value: "wide", labelKey: "playground.fields.sizeWide" },
];

type FieldDef = {
  name: string;
  labelKey: string;
  type: FieldType;
  options?: { value: string; labelKey: string }[];
  min?: number;
  max?: number;
  maxLength?: number;
};

const MEMBER_EVENT_FIELDS: FieldDef[] = [
  { name: "memberCount", labelKey: "playground.fields.memberCount", type: "number", min: 0 },
  { name: "message", labelKey: "playground.fields.message", type: "text", maxLength: 64 },
  {
    name: "secondaryMessage",
    labelKey: "playground.fields.secondaryMessage",
    type: "text",
    maxLength: 72,
  },
  {
    name: "layout",
    labelKey: "playground.fields.layout",
    type: "select",
    options: LAYOUT_OPTIONS,
  },
  {
    name: "size",
    labelKey: "playground.fields.size",
    type: "select",
    options: SIZE_OPTIONS,
  },
  { name: "accentColor", labelKey: "playground.fields.accentColor", type: "color" },
  { name: "usernameColor", labelKey: "playground.fields.usernameColor", type: "color" },
  { name: "messageColor", labelKey: "playground.fields.messageColor", type: "color" },
  {
    name: "secondaryMessageColor",
    labelKey: "playground.fields.secondaryMessageColor",
    type: "color",
  },
  { name: "showDate", labelKey: "playground.fields.showDate", type: "boolean" },
  {
    name: "usernameEffect",
    labelKey: "playground.fields.usernameEffect",
    type: "select",
    options: EFFECT_OPTIONS,
  },
  {
    name: "messageEffect",
    labelKey: "playground.fields.messageEffect",
    type: "select",
    options: EFFECT_OPTIONS,
  },
  { name: "showBadges", labelKey: "playground.fields.showBadges", type: "boolean" },
  { name: "maxBadges", labelKey: "playground.fields.maxBadges", type: "number", min: 0, max: 10 },
  { name: "animated", labelKey: "playground.fields.animated", type: "boolean" },
  { name: "confetti", labelKey: "playground.fields.confetti", type: "boolean" },
  {
    name: "gifSeconds",
    labelKey: "playground.fields.gifSeconds",
    type: "number",
    min: 1,
    max: 10,
  },
];

const CARD_FIELDS: Record<string, FieldDef[]> = {
  profile: [
    {
      name: "customUsername",
      labelKey: "playground.fields.customUsername",
      type: "text",
      maxLength: 32,
    },
    { name: "customTag", labelKey: "playground.fields.customTag", type: "text", maxLength: 20 },
    {
      name: "customSubtitle",
      labelKey: "playground.fields.customSubtitle",
      type: "text",
      maxLength: 40,
    },
    {
      name: "presenceStatus",
      labelKey: "playground.fields.presenceStatus",
      type: "select",
      options: PRESENCE_OPTIONS,
    },
    { name: "usernameColor", labelKey: "playground.fields.usernameColor", type: "color" },
    { name: "tagColor", labelKey: "playground.fields.tagColor", type: "color" },
    { name: "badgesFrame", labelKey: "playground.fields.badgesFrame", type: "boolean" },
    { name: "removeBadges", labelKey: "playground.fields.removeBadges", type: "boolean" },
    { name: "squareAvatar", labelKey: "playground.fields.squareAvatar", type: "boolean" },
    { name: "removeAvatarFrame", labelKey: "playground.fields.removeAvatarFrame", type: "boolean" },
    { name: "level", labelKey: "playground.fields.rankLevel", type: "number", min: 0 },
    { name: "currentXp", labelKey: "playground.fields.rankCurrentXp", type: "number", min: 0 },
    { name: "requiredXp", labelKey: "playground.fields.rankRequiredXp", type: "number", min: 1 },
  ],
  welcome: MEMBER_EVENT_FIELDS,
  leave: MEMBER_EVENT_FIELDS,
  levelup: [
    { name: "message", labelKey: "playground.fields.message", type: "text", maxLength: 58 },
    { name: "accentColor", labelKey: "playground.fields.accentColor", type: "color" },
    { name: "barColor", labelKey: "playground.fields.barColor", type: "color" },
    { name: "barBackgroundColor", labelKey: "playground.fields.barBackgroundColor", type: "color" },
    { name: "levelColor", labelKey: "playground.fields.levelColor", type: "color" },
    { name: "usernameColor", labelKey: "playground.fields.usernameColor", type: "color" },
    { name: "messageColor", labelKey: "playground.fields.messageColor", type: "color" },
    { name: "currentXp", labelKey: "playground.fields.currentXp", type: "number", min: 0 },
    { name: "requiredXp", labelKey: "playground.fields.requiredXp", type: "number", min: 1 },
    { name: "backgroundTint", labelKey: "playground.fields.backgroundTint", type: "boolean" },
    { name: "showBadges", labelKey: "playground.fields.showBadges", type: "boolean" },
    { name: "maxBadges", labelKey: "playground.fields.maxBadges", type: "number", min: 0, max: 10 },
  ],
  achievement: [
    {
      name: "description",
      labelKey: "playground.fields.description",
      type: "text",
      maxLength: 60,
    },
    {
      name: "progressText",
      labelKey: "playground.fields.progressText",
      type: "text",
      maxLength: 40,
    },
    { name: "accentColor", labelKey: "playground.fields.accentColor", type: "color" },
    {
      name: "rarity",
      labelKey: "playground.fields.rarity",
      type: "select",
      options: [
        { value: "common", labelKey: "playground.fields.rarityCommon" },
        { value: "rare", labelKey: "playground.fields.rarityRare" },
        { value: "epic", labelKey: "playground.fields.rarityEpic" },
        { value: "legendary", labelKey: "playground.fields.rarityLegendary" },
      ],
    },
    { name: "showUsername", labelKey: "playground.fields.showUsername", type: "boolean" },
  ],
  ship: [
    { name: "shipName", labelKey: "playground.fields.shipName", type: "text", maxLength: 40 },
    { name: "message", labelKey: "playground.fields.message", type: "text", maxLength: 42 },
    { name: "heartColor", labelKey: "playground.fields.heartColor", type: "color" },
    { name: "accentColor", labelKey: "playground.fields.accentColor", type: "color" },
    {
      name: "percentage",
      labelKey: "playground.fields.percentage",
      type: "number",
      min: 0,
      max: 100,
    },
  ],
  leaderboard: [
    { name: "title", labelKey: "playground.fields.title", type: "text", maxLength: 40 },
    { name: "subtitle", labelKey: "playground.fields.subtitle", type: "text", maxLength: 60 },
    { name: "accentColor", labelKey: "playground.fields.accentColor", type: "color" },
    { name: "barColor", labelKey: "playground.fields.barColor", type: "color" },
    { name: "levelColor", labelKey: "playground.fields.levelColor", type: "color" },
    { name: "usernameColor", labelKey: "playground.fields.usernameColor", type: "color" },
    {
      name: "maxEntries",
      labelKey: "playground.fields.maxEntries",
      type: "number",
      min: 1,
      max: 25,
    },
  ],
  birthday: [
    { name: "age", labelKey: "playground.fields.age", type: "number", min: 0, max: 150 },
    {
      name: "yearsInServer",
      labelKey: "playground.fields.yearsInServer",
      type: "number",
      min: 0,
      max: 100,
    },
    {
      name: "customMessage",
      labelKey: "playground.fields.customMessage",
      type: "text",
      maxLength: 120,
    },
    { name: "accentColor", labelKey: "playground.fields.accentColor", type: "color" },
  ],
  marriage: [
    { name: "marriedSince", labelKey: "playground.fields.marriedSince", type: "date" },
    { name: "vows", labelKey: "playground.fields.vows", type: "text", maxLength: 200 },
    { name: "accentColor", labelKey: "playground.fields.accentColor", type: "color" },
  ],
  weeklyrecap: [
    { name: "messagesSent", labelKey: "playground.fields.messagesSent", type: "number", min: 0 },
    { name: "voiceMinutes", labelKey: "playground.fields.voiceMinutes", type: "number", min: 0 },
    { name: "xpGained", labelKey: "playground.fields.xpGained", type: "number", min: 0 },
    {
      name: "currentStreak",
      labelKey: "playground.fields.currentStreak",
      type: "number",
      min: 0,
    },
    { name: "rankChange", labelKey: "playground.fields.rankChange", type: "number" },
    {
      name: "mostActiveDay",
      labelKey: "playground.fields.mostActiveDay",
      type: "text",
      maxLength: 20,
    },
    { name: "activityMon", labelKey: "playground.fields.activityMon", type: "number", min: 0 },
    { name: "activityTue", labelKey: "playground.fields.activityTue", type: "number", min: 0 },
    { name: "activityWed", labelKey: "playground.fields.activityWed", type: "number", min: 0 },
    { name: "activityThu", labelKey: "playground.fields.activityThu", type: "number", min: 0 },
    { name: "activityFri", labelKey: "playground.fields.activityFri", type: "number", min: 0 },
    { name: "activitySat", labelKey: "playground.fields.activitySat", type: "number", min: 0 },
    { name: "activitySun", labelKey: "playground.fields.activitySun", type: "number", min: 0 },
    { name: "accentColor", labelKey: "playground.fields.accentColor", type: "color" },
  ],
};

export default function Playground() {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<CardMeta[]>(FALLBACK_CATALOG);
  const [serverUp, setServerUp] = useState<boolean | null>(null);

  const [selectedCard, setSelectedCard] = useState("");
  const [userId, setUserId] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [guildId, setGuildId] = useState("");
  const [guildName, setGuildName] = useState("");
  const [customBackground, setCustomBackground] = useState("");
  const [borderColors, setBorderColors] = useState<string[]>(["", "", "", ""]);
  const [removeBorder, setRemoveBorder] = useState(false);
  const [shipHideText, setShipHideText] = useState(false);
  const [themeName, setThemeName] = useState("");
  const [useRoleColor, setUseRoleColor] = useState(true);
  const [useNitroTheme, setUseNitroTheme] = useState(false);
  const [bypassCache, setBypassCache] = useState(false);
  const [watermarkImageUrl, setWatermarkImageUrl] = useState("");
  const [watermarkText, setWatermarkText] = useState("");
  const [watermarkPosition, setWatermarkPosition] = useState("");
  const [watermarkOpacity, setWatermarkOpacity] = useState("");
  const [watermarkSize, setWatermarkSize] = useState("");
  const [watermarkOffsetX, setWatermarkOffsetX] = useState("");
  const [watermarkOffsetY, setWatermarkOffsetY] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [extraFields, setExtraFields] = useState<Record<string, string>>({});

  const [userIdError, setUserIdError] = useState("");
  const [partnerIdError, setPartnerIdError] = useState("");
  const [selectError, setSelectError] = useState("");

  const [result, setResult] = useState<ResultState>({ status: "idle" });

  useEffect(() => {
    fetchCatalog()
      .then((c) => {
        setCatalog(c);
        setServerUp(true);
      })
      .catch(() => setServerUp(false));
  }, []);

  useEffect(() => {
    if (!selectedCard && catalog.length > 0) {
      setSelectedCard(catalog[0].key);
    }
  }, [catalog, selectedCard]);

  const activeCard = catalog.find((c) => c.key === selectedCard);
  const isShip = selectedCard === "ship";
  const isMarriage = selectedCard === "marriage";
  const needsPartnerId = isShip || isMarriage;
  const fields = CARD_FIELDS[selectedCard] ?? [];

  function updateExtra(name: string, value: string) {
    setExtraFields((prev) => ({ ...prev, [name]: value }));
  }

  function updateExtraNumber(field: FieldDef, rawValue: string) {
    if (rawValue === "") {
      updateExtra(field.name, "");
      return;
    }
    let parsed = Number(rawValue);
    if (Number.isNaN(parsed)) {
      updateExtra(field.name, rawValue);
      return;
    }
    if (field.min != null) parsed = Math.max(field.min, parsed);
    if (field.max != null) parsed = Math.min(field.max, parsed);
    updateExtra(field.name, String(parsed));
  }

  function fieldRangeHint(field: FieldDef): string | null {
    if (field.min != null && field.max != null) return `${field.min}–${field.max}`;
    if (field.min != null) return t("playground.fieldMinHint", { min: field.min });
    if (field.max != null) return t("playground.fieldMaxHint", { max: field.max });
    return null;
  }

  function updateBorderColor(index: number, value: string) {
    setBorderColors((prev) => prev.map((color, i) => (i === index ? value : color)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!selectedCard) {
      setSelectError(t("playground.selectCardFirst"));
      return;
    }
    setSelectError("");

    const trimmedUserId = userId.trim();
    if (!isValidDiscordId(trimmedUserId)) {
      setUserIdError(t("playground.invalidUserId"));
      return;
    }
    setUserIdError("");

    const trimmedPartnerId = partnerId.trim();
    if (needsPartnerId) {
      if (!isValidDiscordId(trimmedPartnerId)) {
        setPartnerIdError(t("playground.invalidPartnerId"));
        return;
      }
    }
    setPartnerIdError("");

    setResult({ status: "loading" });

    const relevantExtra: Record<string, string> = {};
    for (const field of fields) {
      const value = extraFields[field.name];
      if (value) relevantExtra[field.name] = value;
    }
    if (isShip && shipHideText) {
      relevantExtra.showText = "false";
    }
    if (themeName) relevantExtra.theme = themeName;
    relevantExtra.useRoleColor = String(useRoleColor);
    if (useNitroTheme) relevantExtra.useNitroTheme = "true";
    if (bypassCache) relevantExtra.bypassCache = "true";
    if (watermarkImageUrl.trim()) relevantExtra.watermarkImageUrl = watermarkImageUrl.trim();
    if (watermarkText.trim()) relevantExtra.watermarkText = watermarkText.trim();
    if (watermarkPosition) relevantExtra.watermarkPosition = watermarkPosition;
    if (watermarkOpacity) relevantExtra.watermarkOpacity = watermarkOpacity;
    if (watermarkSize) relevantExtra.watermarkSize = watermarkSize;
    if (watermarkOffsetX) relevantExtra.watermarkOffsetX = watermarkOffsetX;
    if (watermarkOffsetY) relevantExtra.watermarkOffsetY = watermarkOffsetY;

    const trimmedBorderColors = borderColors.map((color) => color.trim()).filter(Boolean);
    const finalBorderColor: string | string[] | undefined =
      trimmedBorderColors.length > 1 ? trimmedBorderColors : trimmedBorderColors[0];

    const outcome = await fetchCardPreview(selectedCard, {
      userId: trimmedUserId,
      partnerId: needsPartnerId ? trimmedPartnerId : undefined,
      guildId: guildId.trim() || undefined,
      guildName: guildName.trim() || undefined,
      customBackground: customBackground.trim() || undefined,
      borderColor: finalBorderColor,
      removeBorder,
      extra: relevantExtra,
    });

    setResult(
      outcome.ok
        ? { status: "done", url: outcome.url, extension: relevantExtra.animated === "true" ? "gif" : "png" }
        : { status: "error", error: outcome.error },
    );
  }

  return (
    <article>
      <h1>{t("playground.title")}</h1>
      <p>
        <Trans
          i18nKey="playground.intro"
          values={{ count: catalog.length }}
          components={[<code key="0" />]}
        />
      </p>

      {serverUp === false && (
        <div className={styles.warning}>
          <Trans
            i18nKey="playground.serverDown"
            values={{ apiBase: API_BASE }}
            components={[<code key="0" />, <code key="1" />, <code key="2" />]}
          />
        </div>
      )}

      <div className={styles.layout}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <span>{t("playground.cardSelectLabel")}</span>
            <div className={styles.cardGrid}>
              {catalog.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`${styles.cardOption} ${
                    selectedCard === c.key ? styles.cardOptionActive : ""
                  }`}
                  onClick={() => {
                  setSelectedCard(c.key);
                  setSelectError("");
                  setResult({ status: "idle" });
                }}
              >
                <span className={styles.cardOptionLabel}>
                  {t(`playground.cards.${c.key}.label`, c.label)}
                </span>
                <span className={styles.cardOptionDesc}>
                  {t(`playground.cards.${c.key}.description`, c.description)}
                </span>
              </button>
            ))}
          </div>
          {selectError && <span className={styles.fieldError}>{selectError}</span>}
        </div>

        <label className={styles.field}>
          <span>{t("playground.userIdLabel")}</span>
          <input
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setUserIdError("");
            }}
            placeholder="123456789012345678"
            inputMode="numeric"
            required
          />
          {userIdError && <span className={styles.fieldError}>{userIdError}</span>}
        </label>

        {needsPartnerId && (
          <label className={styles.field}>
            <span>{t("playground.partnerIdLabel")}</span>
            <input
              value={partnerId}
              onChange={(e) => {
                setPartnerId(e.target.value);
                setPartnerIdError("");
              }}
              placeholder="123456789012345678"
              inputMode="numeric"
              required
            />
            {partnerIdError && <span className={styles.fieldError}>{partnerIdError}</span>}
          </label>
        )}

        {fields.length > 0 && (
          <div className={styles.advancedGrid}>
            {fields.map((field) =>
              field.type === "boolean" ? (
                <label
                  key={field.name}
                  className={`${styles.field} ${styles.checkboxField}`}
                >
                  <input
                    type="checkbox"
                    checked={extraFields[field.name] === "true"}
                    onChange={(e) => updateExtra(field.name, e.target.checked ? "true" : "")}
                  />
                  <span>{t(field.labelKey)}</span>
                </label>
              ) : (
                <label key={field.name} className={styles.field}>
                  <span>{t(field.labelKey)}</span>
                  {field.type === "color" && (
                    <div className={styles.colorField}>
                      <input
                        type="color"
                        value={extraFields[field.name] || "#7c5cff"}
                        onChange={(e) => updateExtra(field.name, e.target.value)}
                      />
                      <input
                        type="text"
                        value={extraFields[field.name] ?? ""}
                        onChange={(e) => updateExtra(field.name, e.target.value)}
                        placeholder="#7C5CFF"
                      />
                    </div>
                  )}
                  {field.type === "number" && (
                    <>
                      <input
                        type="number"
                        min={field.min}
                        max={field.max}
                        value={extraFields[field.name] ?? ""}
                        onChange={(e) => updateExtraNumber(field, e.target.value)}
                        onBlur={(e) => updateExtraNumber(field, e.target.value)}
                      />
                      {fieldRangeHint(field) && (
                        <span className={styles.fieldHint}>{fieldRangeHint(field)}</span>
                      )}
                    </>
                  )}
                  {field.type === "select" && (
                    <select
                      value={extraFields[field.name] ?? field.options?.[0]?.value ?? ""}
                      onChange={(e) => updateExtra(field.name, e.target.value)}
                    >
                      {field.options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </option>
                      ))}
                    </select>
                  )}
                  {field.type === "text" && (
                    <>
                      <input
                        value={extraFields[field.name] ?? ""}
                        maxLength={field.maxLength}
                        onChange={(e) => updateExtra(field.name, e.target.value)}
                      />
                      {field.maxLength != null && (
                        <span className={styles.fieldHint}>
                          {(extraFields[field.name] ?? "").length}/{field.maxLength}
                        </span>
                      )}
                    </>
                  )}
                  {field.type === "date" && (
                    <input
                      type="date"
                      value={extraFields[field.name] ?? ""}
                      onChange={(e) => updateExtra(field.name, e.target.value)}
                    />
                  )}
                </label>
              ),
            )}
            {isShip && (
              <label className={`${styles.field} ${styles.checkboxField}`}>
                <input
                  type="checkbox"
                  checked={shipHideText}
                  onChange={(e) => setShipHideText(e.target.checked)}
                />
                <span>{t("playground.shipHideTextLabel")}</span>
              </label>
            )}
          </div>
        )}

        <button
          type="button"
          className={styles.advancedToggle}
          onClick={() => setShowAdvanced((v) => !v)}
        >
          {showAdvanced ? t("playground.advancedHide") : t("playground.advancedShow")}
        </button>

        {showAdvanced && (
          <div className={styles.advancedGrid}>
            <label className={styles.field}>
              <span>{t("playground.guildIdLabel")}</span>
              <input value={guildId} onChange={(e) => setGuildId(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>{t("playground.guildNameLabel")}</span>
              <input value={guildName} onChange={(e) => setGuildName(e.target.value)} />
            </label>
            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span>{t("playground.borderColorLabel")}</span>
              <div className={styles.colorPair}>
                {BORDER_COLOR_SLOTS.map((slot, i) => (
                  <div key={i}>
                    {slot.labelKey && (
                      <span className={styles.colorPairLabel}>{t(slot.labelKey)}</span>
                    )}
                    <div className={styles.colorField}>
                      <input
                        type="color"
                        value={borderColors[i] || slot.placeholder}
                        onChange={(e) => updateBorderColor(i, e.target.value)}
                      />
                      <input
                        type="text"
                        value={borderColors[i]}
                        onChange={(e) => updateBorderColor(i, e.target.value)}
                        placeholder={slot.placeholder}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </label>
            <label className={styles.field}>
              <span>{t("playground.fields.theme")}</span>
              <select value={themeName} onChange={(e) => setThemeName(e.target.value)}>
                {THEME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            <div className={`${styles.checkboxGroup} ${styles.fieldWide}`}>
              <label className={`${styles.field} ${styles.checkboxField}`}>
                <input
                  type="checkbox"
                  checked={removeBorder}
                  onChange={(e) => setRemoveBorder(e.target.checked)}
                />
                <span>{t("playground.removeBorderLabel")}</span>
              </label>
              <label className={`${styles.field} ${styles.checkboxField}`}>
                <input
                  type="checkbox"
                  checked={useRoleColor}
                  onChange={(e) => setUseRoleColor(e.target.checked)}
                />
                <span>{t("playground.fields.useRoleColor")}</span>
              </label>
              <label className={`${styles.field} ${styles.checkboxField}`}>
                <input
                  type="checkbox"
                  checked={useNitroTheme}
                  onChange={(e) => setUseNitroTheme(e.target.checked)}
                />
                <span>{t("playground.fields.useNitroTheme")}</span>
              </label>
              <label className={`${styles.field} ${styles.checkboxField}`}>
                <input
                  type="checkbox"
                  checked={bypassCache}
                  onChange={(e) => setBypassCache(e.target.checked)}
                />
                <span>{t("playground.fields.bypassCache")}</span>
              </label>
            </div>
            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span>{t("playground.backgroundLabel")}</span>
              <input
                value={customBackground}
                onChange={(e) => setCustomBackground(e.target.value)}
                placeholder="https://example.com/background.png"
              />
            </label>
            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span>{t("playground.fields.watermarkImageUrl")}</span>
              <input
                value={watermarkImageUrl}
                onChange={(e) => setWatermarkImageUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
              />
            </label>
            <label className={styles.field}>
              <span>{t("playground.fields.watermarkText")}</span>
              <input
                value={watermarkText}
                onChange={(e) => setWatermarkText(e.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span>{t("playground.fields.watermarkPosition")}</span>
              <select
                value={watermarkPosition}
                onChange={(e) => setWatermarkPosition(e.target.value)}
              >
                {WATERMARK_POSITION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>{t("playground.fields.watermarkOpacity")}</span>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={watermarkOpacity}
                onChange={(e) => setWatermarkOpacity(e.target.value)}
                placeholder="0.6"
              />
            </label>
            <label className={styles.field}>
              <span>{t("playground.fields.watermarkSize")}</span>
              <input
                type="number"
                min="1"
                step="1"
                value={watermarkSize}
                onChange={(e) => setWatermarkSize(e.target.value)}
                placeholder="56"
              />
            </label>
            <label className={styles.field}>
              <span>{t("playground.fields.watermarkOffsetX")}</span>
              <input
                type="number"
                step="1"
                value={watermarkOffsetX}
                onChange={(e) => setWatermarkOffsetX(e.target.value)}
                placeholder="0"
              />
            </label>
            <label className={styles.field}>
              <span>{t("playground.fields.watermarkOffsetY")}</span>
              <input
                type="number"
                step="1"
                value={watermarkOffsetY}
                onChange={(e) => setWatermarkOffsetY(e.target.value)}
                placeholder="0"
              />
            </label>
          </div>
        )}

        <button type="submit" className={styles.submit}>
          {t("playground.submit")}
        </button>
      </form>

        <div className={styles.previewPanel}>
          <div className={styles.rowHeader}>
            <div>
              <span className={styles.rowLabel}>
                {activeCard
                  ? t(`playground.cards.${activeCard.key}.label`, activeCard.label)
                  : t("playground.cardSelectLabel")}
              </span>
              {activeCard && (
                <span className={styles.rowDesc}>
                  {t(`playground.cards.${activeCard.key}.description`, activeCard.description)}
                </span>
              )}
            </div>
            {result.status === "done" && activeCard && (
              <a
                className={styles.download}
                href={result.url}
                download={`${activeCard.key}.${result.extension}`}
              >
                {t("playground.download")}
              </a>
            )}
          </div>
          <div className={styles.preview}>
            {result.status === "idle" && (
              <span className={styles.hint}>{t("playground.idle")}</span>
            )}
            {result.status === "loading" && (
              <div className={styles.skeleton} role="status" aria-label={t("playground.loading")} />
            )}
            {result.status === "error" && (
              <span className={styles.errorText}>{result.error}</span>
            )}
            {result.status === "done" && activeCard && (
              <img src={result.url} alt={activeCard.label} />
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
